import { useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Copy, Download, FileText, Package, Printer, Check } from 'lucide-react';
import { useDataService } from '@/lib/service-context';
import { useProBranding } from '@/lib/pro-branding';
import { useProArticles, useProCategories } from '@/hooks/use-pro-articles';
import { CommandeStatusBadge } from '@/components/common/StatusBadge';
import { HeaderActionMenu, type HeaderMenuItem } from '@/components/common/HeaderActionMenu';
import { formatTND } from '@/lib/format';
import { downloadCommandePdf, printCommandePdf } from '@/lib/pdf-export';
import type { RemiseTier } from '@erp/domain/pro';

const REMISE_TIERS: RemiseTier[] = ['max', 'moy', 'min'];

export function OrdreDetailPage() {
  const { t } = useTranslation();
  const { code = '' } = useParams<{ code: string }>();
  const ds = useDataService();
  const navigate = useNavigate();
  const branding = useProBranding();
  const [copied, setCopied] = useState(false);

  const query = useQuery({
    queryKey: ['pro-commandes', code],
    queryFn: () => ds.proCommandes.getByCode(code),
    enabled: !!code,
  });

  const articlesQuery = useProArticles();
  const categoriesQuery = useProCategories();
  const articleMap = useMemo(() => {
    const catName = new Map<string, string>();
    for (const c of categoriesQuery.data ?? []) catName.set(c.id, c.name);
    const m = new Map<string, { name: string; imageUrl: string | null; category: string }>();
    for (const a of articlesQuery.data ?? []) {
      m.set(a.id, {
        name: a.name,
        imageUrl: a.imageUrl,
        category: a.categoryId ? (catName.get(a.categoryId) ?? '') : '',
      });
    }
    return m;
  }, [articlesQuery.data, categoriesQuery.data]);

  if (query.isLoading) {
    return <div className="text-[14px]" style={{ color: 'var(--pro-text-grey)' }}>…</div>;
  }
  const cmd = query.data;
  if (!cmd) {
    return (
      <div className="pro-card p-8 text-center">
        <p style={{ color: 'var(--pro-text-grey)' }}>
          {t('pro.ordres.not_found', 'Commande introuvable')}
        </p>
        <Link
          to="/ordres"
          className="inline-flex items-center gap-1 mt-3 text-[13px] font-medium"
          style={{ color: 'var(--pro-primary)' }}
        >
          <ArrowLeft className="w-4 h-4" />
          {t('pro.ordres.back_list', 'Retour à la liste')}
        </Link>
      </div>
    );
  }

  const clientFullName = cmd.client
    ? [cmd.client.prenom, cmd.client.nom].filter(Boolean).join(' ').trim()
    : '—';
  const deliveryAddress =
    [cmd.deliveryAddress, cmd.deliveryCity, cmd.deliveryPostalCode]
      .filter(Boolean)
      .join(', ') ||
    [cmd.client?.adresse, cmd.client?.ville, cmd.client?.cp].filter(Boolean).join(', ') ||
    '—';

  return (
    <div className="space-y-4">
      <div className="pro-card p-6 pro-fade-in">
        {/* Header: ID + status + actions */}
        <div className="flex items-center justify-between flex-wrap gap-3 pb-5">
          <div className="flex items-center gap-3">
            <span
              className="text-[14px] font-semibold"
              style={{ color: 'var(--pro-text-dark)', fontFamily: 'Figtree' }}
            >
              {t('pro.ordres.id_label', 'ID de commande')}
            </span>
            <span
              className="text-[14px] font-semibold"
              style={{ color: 'var(--pro-text-dark)', fontFamily: 'Figtree' }}
            >
              #{cmd.code}
            </span>
            <CommandeStatusBadge status={cmd.status} />
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={async () => {
                try {
                  await printCommandePdf(cmd, (id) => articleMap.get(id) ?? null, {
                    company: branding.pdfCompany,
                    primaryColor: branding.pdfPrimaryColor,
                    template: branding.pdfTemplate,
                  });
                } catch (err) {
                  // eslint-disable-next-line no-console
                  console.error('PDF print failed', err);
                }
              }}
              aria-label={t('pro.doc.print', 'Imprimer')}
              title={t('pro.doc.print', 'Imprimer')}
              className="inline-flex items-center justify-center h-9 w-9 shrink-0 rounded-lg"
              style={{
                border: '1px solid var(--pro-border)',
                color: 'var(--pro-text-dark)',
                background: 'white',
              }}
            >
              <Printer className="w-4 h-4" />
            </button>
            <button
              type="button"
              onClick={async () => {
                try {
                  await downloadCommandePdf(cmd, (id) => articleMap.get(id) ?? null, {
                    company: branding.pdfCompany,
                    primaryColor: branding.pdfPrimaryColor,
                    template: branding.pdfTemplate,
                  });
                } catch (err) {
                  // eslint-disable-next-line no-console
                  console.error('PDF download failed', err);
                  window.print();
                }
              }}
              className="inline-flex items-center gap-2 h-9 px-3 rounded-lg text-[13px] font-medium text-white"
              style={{ background: 'var(--pro-primary)', fontFamily: 'Figtree' }}
            >
              <Download className="w-4 h-4" />
              {t('pro.ordres.download', 'Télécharger')}
            </button>
            <HeaderActionMenu
              ariaLabel={t('pro.ordres.more', 'Plus')}
              items={(() => {
                const out: HeaderMenuItem[] = [
                  {
                    key: 'copy',
                    label: copied
                      ? t('pro.ordres.copied', 'Copié !')
                      : t('pro.ordres.copy_code', "Copier l'identifiant"),
                    icon: copied ? Check : Copy,
                    onClick: async () => {
                      try {
                        await navigator.clipboard.writeText(`ORD${cmd.code}`);
                        setCopied(true);
                        setTimeout(() => setCopied(false), 2000);
                      } catch {
                        // ignore
                      }
                    },
                  },
                ];
                if (cmd.parentDevisCode) {
                  out.push({
                    key: 'view-devis',
                    label: t('pro.ordres.view_parent_devis', 'Voir le devis source'),
                    icon: FileText,
                    onClick: () =>
                      navigate(`/devis/${encodeURIComponent(cmd.parentDevisCode!)}`),
                  });
                }
                return out;
              })()}
            />
          </div>
        </div>

        {/* Client + delivery details */}
        <div className="space-y-3 pb-5">
          <DetailRow
            label={t('pro.ordres.client', 'Client')}
            value={clientFullName}
            valueBold
          />

          <h3
            className="text-[14px] font-semibold"
            style={{ color: 'var(--pro-text-dark)', fontFamily: 'Figtree' }}
          >
            {t('pro.ordres.delivery_details', 'Details de livraison')}
          </h3>

          <div className="flex flex-wrap items-baseline gap-x-8 gap-y-2">
            <DetailRow
              label={t('pro.ordres.firstname_lastname', 'Prenom et Nom')}
              value={clientFullName}
            />
            <DetailRow
              label={t('pro.ordres.phone', 'Tél.')}
              value={cmd.client?.telephone ?? '—'}
            />
          </div>
          <DetailRow
            label={t('pro.ordres.address', 'Adresse')}
            value={deliveryAddress}
          />
          <div className="flex flex-wrap items-baseline gap-x-8 gap-y-2">
            <DetailRow
              label={t('pro.ordres.company', 'Société')}
              value={cmd.client?.complement ?? '—'}
            />
            <DetailRow
              label={t('pro.ordres.tax_id', 'MF :')}
              value={cmd.notes ?? '—'}
            />
          </div>
        </div>

        {/* Products: mobile cards */}
        <div className="md:hidden space-y-3 pt-4" style={{ borderTop: '1px solid var(--pro-line)' }}>
          {(cmd.lines ?? []).map((l) => {
            const art = articleMap.get(l.articleId);
            const unitPrice = l.pttc / Math.max(1, l.quantite);
            return (
              <div
                key={l.id}
                className="rounded-xl p-3 space-y-3"
                style={{ border: '1px solid var(--pro-line)' }}
              >
                <div className="flex items-center gap-3">
                  <div
                    className="w-12 h-12 rounded-lg shrink-0 flex items-center justify-center overflow-hidden"
                    style={{ background: 'var(--pro-grey-subtle)' }}
                  >
                    {art?.imageUrl ? (
                      <img src={art.imageUrl} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <Package className="w-5 h-5" style={{ color: 'var(--pro-text-grey-light)' }} />
                    )}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div
                      className="text-[14px] font-medium truncate"
                      style={{ color: 'var(--pro-text-dark)', fontFamily: 'Figtree' }}
                    >
                      {art?.name ?? l.articleId}
                    </div>
                    <div className="text-[12px] truncate" style={{ color: 'var(--pro-text-grey)' }}>
                      {art?.category ?? ''}
                    </div>
                  </div>
                </div>
                <div className="flex items-center justify-between gap-2">
                  <span className="text-[13px]" style={{ color: 'var(--pro-text-grey)' }}>
                    {l.quantite} × {formatTND(unitPrice)}
                  </span>
                  <span className="text-[14px] font-semibold" style={{ color: 'var(--pro-text-dark)' }}>
                    {formatTND(l.pttc)}
                  </span>
                </div>
              </div>
            );
          })}
        </div>

        {/* Products table (desktop) */}
        <div className="hidden md:block overflow-x-auto" style={{ borderTop: '1px solid var(--pro-line)' }}>
          <table className="w-full text-[13px]">
            <thead>
              <tr style={{ borderBottom: '1px solid var(--pro-line)' }}>
                <Th>{t('pro.ordres.col_products', 'Produits')}</Th>
                <Th align="center">{t('pro.ordres.col_qty', 'Qté')}</Th>
                <Th align="right">{t('pro.ordres.col_price', 'Prix')}</Th>
                <Th align="right">{t('pro.ordres.col_total', 'Total')}</Th>
              </tr>
            </thead>
            <tbody>
              {(cmd.lines ?? []).map((l) => {
                const art = articleMap.get(l.articleId);
                const unitPrice = l.pttc / Math.max(1, l.quantite);
                return (
                  <tr key={l.id} style={{ borderBottom: '1px solid var(--pro-line)' }}>
                    <td className="py-3 px-3">
                      <div className="flex items-center gap-3">
                        <div
                          className="w-12 h-12 rounded-lg shrink-0 flex items-center justify-center overflow-hidden"
                          style={{ background: 'var(--pro-grey-subtle)' }}
                        >
                          {art?.imageUrl ? (
                            <img src={art.imageUrl} alt="" className="w-full h-full object-cover" />
                          ) : (
                            <Package className="w-5 h-5" style={{ color: 'var(--pro-text-grey-light)' }} />
                          )}
                        </div>
                        <div className="min-w-0">
                          <div
                            className="text-[14px] font-medium truncate"
                            style={{ color: 'var(--pro-text-dark)', fontFamily: 'Figtree' }}
                          >
                            {art?.name ?? l.articleId}
                          </div>
                          <div
                            className="text-[12px] truncate"
                            style={{ color: 'var(--pro-text-grey)', fontFamily: 'Figtree' }}
                          >
                            {art?.category ?? ''}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="py-3 px-3 text-center" style={{ color: 'var(--pro-text-dark)' }}>
                      {l.quantite}
                    </td>
                    <td className="py-3 px-3 text-right" style={{ color: 'var(--pro-text-dark)' }}>
                      {formatTND(unitPrice)}
                    </td>
                    <td className="py-3 px-3 text-right font-medium" style={{ color: 'var(--pro-text-dark)' }}>
                      {formatTND(l.pttc)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Summary */}
        <div className="flex justify-end mt-6">
          <div
            className="w-full max-w-[400px] rounded-xl p-4 space-y-3"
            style={{ border: '1px solid var(--pro-line)', background: 'var(--pro-bg)' }}
          >
            <div className="flex items-center justify-between">
              <span
                className="text-[14px]"
                style={{ color: 'var(--pro-text-dark)', fontFamily: 'Figtree' }}
              >
                {t('pro.ordres.discount', 'Remise')}
              </span>
              <RemiseToggle current={cmd.typeRemise} />
            </div>
            <SummaryRow
              label={t('pro.ordres.subtotal', 'Sous - Total')}
              value={formatTND(cmd.pht)}
            />
            <SummaryRow
              label={t('pro.ordres.delivery', 'Livraison')}
              value={formatTND(cmd.deliveryFee)}
            />
            <div
              className="flex items-center justify-between pt-3"
              style={{ borderTop: '1px solid var(--pro-line)' }}
            >
              <span
                className="text-[14px] font-semibold"
                style={{ color: 'var(--pro-text-dark)', fontFamily: 'Figtree' }}
              >
                {t('pro.ordres.total_payer', "Total d'un payeur")}
              </span>
              <span
                className="text-[16px] font-bold"
                style={{ color: 'var(--pro-text-dark)', fontFamily: 'Figtree' }}
              >
                {formatTND(cmd.totalAPayer)}
              </span>
            </div>
            {cmd.totalCommission > 0 && (
              <div
                className="flex items-center justify-between rounded-lg px-3 py-2"
                style={{ background: 'var(--pro-primary-subtle)' }}
              >
                <span
                  className="text-[14px] font-medium"
                  style={{ color: 'var(--pro-primary)', fontFamily: 'Figtree' }}
                >
                  {t('pro.ordres.your_commission', 'Votre Commission')}
                </span>
                <span
                  className="text-[14px] font-semibold"
                  style={{ color: 'var(--pro-primary)', fontFamily: 'Figtree' }}
                >
                  {formatTND(cmd.totalCommission)}
                </span>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function DetailRow({
  label,
  value,
  valueBold,
}: {
  label: string;
  value: string;
  valueBold?: boolean;
}) {
  return (
    <div className="flex items-baseline gap-2">
      <span
        className="text-[13px]"
        style={{ color: 'var(--pro-text-grey)', fontFamily: 'Figtree' }}
      >
        {label}
      </span>
      <span
        className={valueBold ? 'text-[14px] font-medium' : 'text-[13px]'}
        style={{ color: 'var(--pro-text-dark)', fontFamily: 'Figtree' }}
      >
        {value}
      </span>
    </div>
  );
}

function RemiseToggle({ current }: { current: RemiseTier }) {
  return (
    <div className="inline-flex items-center gap-1">
      {REMISE_TIERS.map((tier) => {
        const active = tier === current;
        return (
          <span
            key={tier}
            className="inline-flex items-center justify-center h-7 px-3 rounded-lg text-[12px] font-medium select-none"
            style={
              active
                ? {
                    border: '1px solid var(--pro-primary)',
                    color: 'var(--pro-primary-text)',
                    background: 'var(--pro-primary-subtle)',
                  }
                : {
                    border: '1px solid transparent',
                    color: 'var(--pro-text-muted)',
                    background: 'transparent',
                  }
            }
          >
            {tier}
          </span>
        );
      })}
    </div>
  );
}

function SummaryRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between">
      <span
        className="text-[13px]"
        style={{ color: 'var(--pro-text-grey)', fontFamily: 'Figtree' }}
      >
        {label}
      </span>
      <span
        className="text-[14px]"
        style={{ color: 'var(--pro-text-dark)', fontFamily: 'Figtree' }}
      >
        {value}
      </span>
    </div>
  );
}

function Th({
  children,
  align = 'left',
}: {
  children: React.ReactNode;
  align?: 'left' | 'center' | 'right';
}) {
  return (
    <th
      className={`py-3 px-3 text-[12px] font-medium ${
        align === 'right' ? 'text-right' : align === 'center' ? 'text-center' : 'text-left'
      }`}
      style={{ color: 'var(--pro-text-grey)', fontFamily: 'Figtree' }}
    >
      {children}
    </th>
  );
}
